import React, { useState, useEffect, useRef } from 'react';

const BreathingExercise = ({ 
  breathParameters = [], 
  isPaused = false,
  firstStepDuration = 0 // Default value if not provided
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0); 
  const lastTimeRef = useRef(null); 

  const hasData = breathParameters && breathParameters.length > 0;
  const isFinished = hasData && currentIndex >= breathParameters.length;
  // Change 1: It is "Active" if it has data OR if it's in Demo/Prepare mode (no data)
  const isDemo = !hasData;
  const isRunning = (hasData && !isFinished) || isDemo;
  
  // Change 1: In demo mode, fixed 2s duration. Otherwise, use real data.
  const currentDuration = isDemo ? 2 : (hasData && !isFinished ? breathParameters[currentIndex] : 0);
  const isInhaling = currentIndex % 2 === 0;

  // --- Look Ahead Logic ---
  const nextIndex = currentIndex + 1;
  const hasNext = hasData && nextIndex < breathParameters.length;
  const nextDuration = hasNext ? breathParameters[nextIndex] : 0;
  const nextIsInhale = nextIndex % 2 === 0;

  // --- 1. Step Initialization ---
  useEffect(() => {
    // Reset when data arrives or finishes
    if (hasData && !isFinished) {
      setTimeLeft(breathParameters[currentIndex]);
      lastTimeRef.current = null;
    } else if (isDemo) {
      // In demo mode, we just reset to 2s whenever index changes
      setTimeLeft(2); 
      lastTimeRef.current = null;
    }
  }, [currentIndex, hasData, isFinished, breathParameters, isDemo]);

  // --- 2. Master Timer ---
  useEffect(() => {
    if (!isRunning || isPaused) {
      lastTimeRef.current = null; 
      return;
    }

    const interval = setInterval(() => {
      const now = Date.now();
      const dt = lastTimeRef.current ? (now - lastTimeRef.current) / 1000 : 0;
      lastTimeRef.current = now;

      setTimeLeft((prev) => {
        const newTime = prev - dt;
        if (newTime <= 0) {
          setCurrentIndex((current) => current + 1);
          return 0; 
        }
        return newTime;
      });
    }, 50); 

    return () => clearInterval(interval);
  }, [isRunning, isPaused]);


  // --- Dynamic Styles ---
  const getOrbStyle = () => {
    const baseStyle = {
      ...styles.orbBase,
      transition: isRunning 
        ? `all ${currentDuration}s cubic-bezier(0.4, 0.0, 0.2, 1)` 
        : 'all 0.5s ease-out',
    };

    if (isPaused) return { ...baseStyle, ...styles.orbPausedState(isInhaling) };
    if (isFinished) return { ...baseStyle, ...styles.orbFinishedState };

    // Change 1: Handle Demo Mode (Grey Animation)
    if (isDemo) {
        return {
            ...baseStyle,
            // Animate scale, but keep colors grey/neutral
            transform: isInhaling ? 'scale(1.5)' : 'scale(1.0)',
            background: isInhaling 
                ? 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)' // Light Grey Gradient
                : '#e2e8f0',
            boxShadow: isInhaling 
                ? '0 0 40px 5px rgba(203, 213, 225, 0.4)' 
                : 'inset 0 0 10px rgba(0,0,0,0.05)',
        };
    }

    // Normal Active State
    if (isInhaling) {
      return {
        ...baseStyle,
        transform: 'scale(1.5)',
        background: 'linear-gradient(135deg, #22d3ee 0%, #0ea5e9 100%)',
        boxShadow: '0 0 60px 10px rgba(34, 211, 238, 0.4), inset 0 0 20px rgba(255,255,255,0.3)',
      };
    } 
    return {
      ...baseStyle,
      transform: 'scale(1.0)',
      background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)',
      boxShadow: '0 0 30px 5px rgba(99, 102, 241, 0.3), inset 0 0 10px rgba(255,255,255,0.1)',
    };
  };

  let mainText = "Prepare";
  let subText = "";
  
  if (isFinished) {
    mainText = "Done";
  } else if (isPaused) {
    mainText = "Paused";
    subText = `${Math.ceil(timeLeft)}s`;
  } else if (isRunning) {
    // Show text for both Demo and Real runs
    mainText = isInhaling ? "Inhale" : "Exhale";
    subText = `${Math.ceil(timeLeft)}s`;
  }

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes fadeSlideUpEntry {
            from { opacity: 0; transform: translateY(10px); filter: blur(2px); }
            to { opacity: 1; transform: translateY(0); filter: blur(0); }
          }
          .smooth-entry {
            animation: fadeSlideUpEntry 0.5s ease forwards;
          }
        `}
      </style>

      {/* Main Animation Scene */}
      <div style={styles.sceneWrapper}>
        <div style={styles.trackRing}></div>
        <div style={getOrbStyle()}>
          <div key={`${currentIndex}-${isPaused}`} className="smooth-entry" style={styles.textWrapper}>
            {/* Darker text for Demo mode so it's readable on light grey */}
            <span style={{...styles.actionText, color: isDemo ? '#94a3b8' : 'white'}}>
                {mainText}
            </span>
            {subText && (
                <span style={{...styles.timerText, color: isDemo ? '#94a3b8' : 'rgba(255,255,255,0.9)'}}>
                    {subText}
                </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Pill Logic: Next Step OR First Step (Change 2) */}
      <div style={{ height: '24px', marginTop: '10px', marginBottom: '15px' }}>
        {/* Scenario A: Demo Mode -> Show "First Step" */}
        {isDemo && (
             <div className="smooth-entry" style={styles.nextStepPill}>
             <span style={{ opacity: 0.6, marginRight: '6px' }}>First Step:</span>
             <span style={{ fontWeight: '600', color: '#475569' }}>Inhale</span>
             <span style={{ marginLeft: '4px', color: '#64748b', fontSize: '0.85rem' }}>
               ({Math.ceil(firstStepDuration)}s)
             </span>
           </div>
        )}

        {/* Scenario B: Running Real Data -> Show "Next" */}
        {!isDemo && isRunning && hasNext && (
          <div className="smooth-entry" style={styles.nextStepPill}>
            <span style={{ opacity: 0.6, marginRight: '6px' }}>Next:</span>
            <span style={{ fontWeight: '600', color: '#475569' }}>
               {nextIsInhale ? "Inhale" : "Exhale"}
            </span>
            <span style={{ marginLeft: '4px', color: '#64748b', fontSize: '0.85rem' }}>
              ({Math.ceil(nextDuration)}s)
            </span>
          </div>
        )}
        {!isDemo && isRunning && !hasNext && (
           <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Final Step</span>
        )}
      </div>

      {/* Status Footer */}
      <div className="text-muted small">
         {isDemo 
            ? "Preview - Waiting to start..." 
            : isRunning 
                ? `Step ${currentIndex + 1} / ${breathParameters.length}` 
                : "Sequence Complete"
         }
      </div>
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sceneWrapper: {
    position: 'relative',
    width: '280px',
    height: '280px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  trackRing: {
    position: 'absolute',
    width: '240px',
    height: '240px',
    borderRadius: '50%',
    border: '2px solid #f1f5f9',
    zIndex: 0,
  },
  orbBase: {
    position: 'relative',
    zIndex: 1,
    width: '140px',
    height: '140px',
    borderRadius: '50%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // orbIdleState is effectively replaced by the "isDemo" logic in getOrbStyle
  orbIdleState: { 
    transform: 'scale(1.0)',
    background: '#e2e8f0',
    boxShadow: 'inset 0 0 10px rgba(0,0,0,0.05)',
  },
  orbPausedState: (isInhaling) => ({
    transform: isInhaling ? 'scale(1.4)' : 'scale(1.1)',
    background: '#cbd5e1', 
    boxShadow: '0 0 10px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
  }),
  orbFinishedState: {
    transform: 'scale(1.0)',
    background: '#10b981',
    boxShadow: '0 0 20px rgba(16, 185, 129, 0.4)',
  },
  textWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  actionText: {
    fontSize: '1.4rem',
    fontWeight: '700',
    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  timerText: {
    fontSize: '1.1rem',
    fontWeight: '500',
  },
  nextStepPill: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '0.8rem',
    color: '#334155',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  }
};

export default BreathingExercise;